import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Res, PaymentOperationDTO, PaymentMethodDTO } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Cols, Col, ErrorBlock } from 'core/ui';
import * as Payment from './PrepaymentStatus';
import classnames from 'classnames';
import { Modal } from 'core/ui-next/modal';

type Props = {
	mode: 'ADD' | 'REMOVE',
	paymentOperation: PaymentOperationDTO,
	pendingAmount: number,
	pendingPaymentParts?: { currentPart: number, totalParts: number }
} & Modal.Actions<Payment.Status>;


// Aquí hay muchas cantidades y es lioso:

// OJO: todas las cantidades se tratan siempre en positivo, incluso en el
// caso de devoluciones. En el momento de generar el CreatedPayment al final
// para cerrar la pantalla es cuando se ajustan a negativo si se trata
// de una devolución.

// Hay una cantidad total que llega por props (recomendada, no se valida)
// por la que realizar el pago o la devolución.
// En el caso de pagos, el máximo es el importe
// pendiente, y en el caso de devoluciones, el importe entregado en pagos
// realizados previamente. Este total se puede superar porque hay escenarios
// en los que añades pagos a un ticket que todavía está vacío para ir
// consumiendo luego ese crédito.

// Luego tenemos el importe por el que se quiere hacer el pago o la devolución

export type State = Payment.Status & { Method: PaymentMethodDTO };

export class CreatePaymentScreen extends PosScreenClassComponent<Props, State> {
	protected txtAmountToPay = React.createRef<UI.PriceBox<State>>();
	protected txtPaidAmount = React.createRef<UI.PriceBox<State>>();
	protected txtTipAmount = React.createRef<UI.PriceBox<State>>();
	protected form = React.createRef<UI.Form<State>>();
	protected cmbMethod = React.createRef<UI.ComboBox<State>>();

	protected getScreenId(): string {
		return 'CREATE_PAYMENT';
	}

	protected getTitle(): string {

		const mainTitle = this.props.mode === 'ADD'
			? Res.Web.PROCESS_PAYMENT_ADD()
			: Res.Web.PROCESS_PAYMENT_REMOVE();
		const parts = this.props.pendingPaymentParts ? Res.Web.PROCESS_PAYMENT_SPLIT_PAYMENT_PARTS_LBL(this.props.pendingPaymentParts.currentPart, this.props.pendingPaymentParts.totalParts) : '';

		return `${mainTitle} ${parts}`.trim();
	}

	protected getImage(): string | SvgIcon {
		return this.props.mode === 'ADD'
			? Images.AddPayment
			: Images.RemovePayment;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const actionTitle = this.props.mode === 'ADD' ? Res.Web.PROCESS_PAYMENT_PAY_BTN() : Res.Web.PROCESS_PAYMENT_REMOVE_ACTION_TEXT();
		const actionImage = this.props.mode === 'ADD' ? Images.AddPayment : Images.RemovePayment;

		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			{ title: actionTitle, image: actionImage, action: this.processPayment }
		];
	}

	protected useKeypad() {
		return true;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	processPayment = async (): Promise<void> => {

		const ok = await this.form.current?.updateModel();
		if (!ok)
			return;

		const canPay = await Payment.canPay(this.state);
		if (canPay)
			this.close(this.state);
	};

	getAmountToPay = (): number => {
		const amounToProcesResult = this.txtAmountToPay.current!.getValue();
		return !amounToProcesResult.ok ? 0 : amounToProcesResult.value;
	};

	getPaidAmount = (): number => {
		const paidAmountResult = this.txtPaidAmount.current!.getValue();
		return !paidAmountResult.ok ? 0 : paidAmountResult.value;
	};

	getTipAmount = (): number => {
		const tipAmountResult = this.txtTipAmount.current!.getValue();
		return tipAmountResult.ok ? tipAmountResult.value : this.state.TipAmount;
	};

	completeTip = async () => {
		this.setState(await Payment.completeTipAmount({ ...this.state, TipAmount: this.getTipAmount() }), async () => await this.form.current!.remountAll());
	};

	updateChange = async () => {
		this.setState(await Payment.updateChangeAmount(this.state, this.getAmountToPay(), this.getPaidAmount()));
	};

	updateTip = async () => {
		this.setState(await Payment.updateTipAmount(this.state, this.getTipAmount()));
	};

	completeAmountToPay = async () => {
		this.setState(await Payment.completeAmountToPay(this.state, Math.abs(this.props.pendingAmount)), async () => {
			await this.form.current!.remount('AmountToPay', 'PaidAmount');
		});
	};

	completePaidAmount = async () => {
		this.setState(await Payment.completePaidAmount(this.state, this.getAmountToPay()), async () => {
			await this.form.current!.remount('AmountToPay', 'PaidAmount');
		});
	};

	selectedMethod = async (method: PaymentMethodDTO) => {
		this.setState(await Payment.selectedMethod(this.state, method));
	};

	createInitialState(props: Props): Nullable<State> {
		return {
			Method: props.paymentOperation.Methods[0],
			AmountToPay: props.pendingPaymentParts ? props.pendingAmount : 0,
			PaidAmount: props.pendingPaymentParts ? props.pendingAmount : 0,
			ChangeAmount: 0,
			TipAmount: 0,
			PendingAmount: props.pendingAmount,
			ThereIsEnoughChange: true,
			ErrorMessage: ''
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected getContent(): React.ReactNode {
		const { Form, PriceBox, ComboBox } = UI.controlsFor<State>();

		// Etiquetas para los controles. No se puede usar la clase control-label porque bootstrap sólo
		// aplica el padding para alinear los lables en pantallas grandes
		const ControlLabel = ({ text, className }: { text: string, className?: string }) => <div style={{ marginTop: 7 }} className={className || ''}>{text}</div>;

		// Añade una fila al formulario repartiendo espacio entre etiqueta e input
		const addRow = (labelText: string, right: React.ReactNode, rowClassName?: string) =>
			<Cols className={rowClassName || ''}>
				<Col width='*'><ControlLabel text={labelText} /></Col>
				<Col width='*'>{right}</Col>
			</Cols>;

		return <Form model={this.state} ref={this.form} className='m-sm' >
			<Cols className='m-b-sm text-16'>
				<Col className='font-bold' width='*'>
					{
						this.props.mode === 'ADD'
							? Res.Web.PROCESS_PAYMENT_ADD_TOTAL_AMOUNT_TEXT(this.props.pendingAmount)
							: Res.Web.PROCESS_PAYMENT_REMOVE_TOTAL_AMOUNT_TEXT(this.props.pendingAmount)
					}
				</Col>
				<Col className='text-right' width='*'>{Res.Web.PROCESS_PAYMENT_CHANGE_AMOUNT_TEXT(this.state.ChangeAmount)}</Col>
			</Cols>

			{addRow(this.props.mode === 'ADD' ? Res.Web.PROCESS_PAYMENT_ADD_AMOUNT_TO_PROCESS_TEXT() : Res.Web.PROCESS_PAYMENT_REMOVE_AMOUNT_TO_PROCESS_TEXT(),
				<PriceBox
					ref={this.txtAmountToPay}
					linkTo={x => x.AmountToPay}
					allowNegative={false}
					onValueChange={this.updateChange}
					className='text-right'
					childrenPosition='prepend'
					rules={[UI.Constraints.maxDecimalPlaces(2), UI.Constraints.maxLength(10), UI.Constraints.greaterThanZero]}
					disabled={!!this.props.pendingPaymentParts}
					required>
					<UI.TextBoxButton onClick={this.completeAmountToPay} iconClassName='fa fa-arrow-right text-info' />
				</PriceBox>,
				'border-bottom')}

			{addRow(Res.Web.PROCESS_PAYMENT_PAYMENT_METHOD_LBL(),
				<ComboBox
					ref={this.cmbMethod}
					linkTo={x => x.Method}
					onValueChanged={(methods: PaymentMethodDTO[]) => this.selectedMethod(methods[0])}
					options={this.props.paymentOperation.Methods}
					required />,
				'm-t')}

			{addRow(Res.Web.PROCESS_PAYMENT_PAID_AMOUNT_LBL(),
				<PriceBox
					ref={this.txtPaidAmount}
					linkTo={x => x.PaidAmount}
					allowNegative={false}
					onValueChange={this.updateChange}
					rules={[UI.Constraints.maxDecimalPlaces(2), UI.Constraints.maxLength(10)]}
					className='text-right'
					syncTextFrom='AmountToPay'
					childrenPosition='prepend'>
					<UI.TextBoxButton onClick={this.completePaidAmount} iconClassName='fa fa-arrow-right text-info' />
				</PriceBox>)}

			{this.state.Method.RegisterTip
				? addRow(Res.Web.PROCESS_PAYMENT_TIP_AMOUNT_LBL(),
					<PriceBox
						ref={this.txtTipAmount}
						linkTo={x => x.TipAmount}
						allowNegative={false}
						onValueChange={this.updateTip}
						rules={[UI.Constraints.maxDecimalPlaces(2), UI.Constraints.maxLength(10)]}
						className='text-right'
						childrenPosition='prepend'>
						<UI.TextBoxButton onClick={this.completeTip} iconClassName='fa fa-arrow-right text-info' />
					</PriceBox>)
				: null
			}
			<p className={classnames({ 'has-error': this.state.ErrorMessage, 'text-center': true })}>
				<ErrorBlock msg={this.state.ErrorMessage} />
			</p>
		</Form >;
	}

}